"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.callableDefault = exports.callableOptional = void 0;
const zod_1 = require("zod");
/**
 * Null-tolerant parameter schemas for Firebase callables.
 *
 * THE TRAP
 *
 * The `@firebase/functions` client serialiser encodes a property whose value is
 * `undefined` as `null` on the wire, because `undefined == null`. So
 *
 *   httpsCallable(fn)({ companyId, from: undefined, to: undefined })
 *
 * does NOT send an absent key. The server receives `{ from: null, to: null }`.
 *
 * A plain `z.string().optional()` accepts only `undefined`. It REJECTS null with
 * `invalid_type: Expected string, received null`, which surfaces as a bare
 * INTERNAL / 400 to the user with no indication of which field was at fault.
 *
 * WHY A UNIT TEST DOES NOT CATCH IT
 *
 * `schema.safeParse({ from: undefined })` PASSES, because it bypasses the SDK's
 * undefined-to-null wire encoding entirely. The failure only appears end to end.
 * That asymmetry is why this class of bug has recurred in this repo: each fix
 * was written inline against `''` or `undefined` rather than `null`. Hence a
 * named helper with tests, rather than another local preprocessor.
 *
 * USAGE
 *
 *   z.object({
 *     companyId: z.string().min(1),                       // genuinely required
 *     from: callableOptional(IsoDateSchema),               // may be absent
 *     limit: callableDefault(z.number().int().max(1000), 500),
 *   })
 *
 * Blank strings are treated as absent too: a cleared form field arrives as `''`
 * and means "no bound", not "match the empty string".
 */
/** True for the three ways a callable caller can say "not supplied". */
const isAbsent = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
/**
 * An optional callable parameter that accepts `null` and `''` as absent.
 *
 * Parses to `T | undefined`, so existing truthiness checks (`if (body.from)`)
 * keep working unchanged.
 */
const callableOptional = (schema) => zod_1.z.preprocess((v) => (isAbsent(v) ? undefined : v), schema.optional());
exports.callableOptional = callableOptional;
/**
 * A callable parameter with a fallback, where `null` and `''` also mean "use the
 * fallback". `z.boolean().default(false)` rejects null; this does not.
 */
const callableDefault = (schema, fallback) => zod_1.z.preprocess((v) => (isAbsent(v) ? fallback : v), schema);
exports.callableDefault = callableDefault;
